import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { CURRENT_VERSION, PATCH_NOTES } from '../patchNotesDatabase';

// ── GM renders ────────────────────────────────────────────────────────
import gmAdamPearce    from '../assets/gm/94400_ADAM_PEARCE.png';
import gmNickAldis     from '../assets/gm/121300_NICK_ALDIS.png';
import gmShawnMichaels from '../assets/gm/45750_SHAWN_MICHAELS.png';
import gmPaulHeyman    from '../assets/gm/37410_PAUL_HEYMAN_14.png';

// ── Brand logos ───────────────────────────────────────────────────────
import logoRaw       from '../assets/brands/logos/RAW25_LOGO.png';
import logoSmackDown from '../assets/brands/logos/SMACKDOWN25_LOGO.png';
import logoNXT       from '../assets/brands/logos/NXT_LOGO.png';
import logoECW       from '../assets/brands/logos/ECW_LOGO.png';

// ── Brand backgrounds ─────────────────────────────────────────────────
import bgRaw       from '../assets/brands/backgrounds/RAW25_BACKGROUND.png';
import bgSmackDown from '../assets/brands/backgrounds/SMACKDOWN25_BACKGROUND.png';
import bgNXT       from '../assets/brands/backgrounds/NXT_BACKGROUND.png';

// ── Static assets ─────────────────────────────────────────────────────
const mainLogo  = process.env.PUBLIC_URL + '/main_logo.png';
const hofTrophy = process.env.PUBLIC_URL + '/hof/trophy.png';
// Wrestler renders — wrestler_id from wwe2k26_roster.json
const R = (id) => process.env.PUBLIC_URL + '/wrestlers/' + id + '-render.png';
const SETH_RENDER   = R(369);   // Seth Rollins
const ROMAN_RENDER  = R(368);   // Roman Reigns
const IYO_RENDER    = R(814);   // Iyo Sky
const LIV_RENDER    = R(734);   // Liv Morgan
const ETHAN_RENDER  = R(1290);  // Ethan Page
const JEVON_RENDER  = R(1235);  // Je'Von Evans

// ── CSS ───────────────────────────────────────────────────────────────
const css = `
  @keyframes wm-fadein {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes wm-modal-in {
    from { opacity: 0; transform: translateY(16px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .wm-overlay {
    position: fixed; inset: 0; z-index: 9000;
    background: rgba(0,0,0,0.9);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: wm-fadein 0.2s ease;
  }
  .wm-modal {
    width: clamp(640px, 72vw, 960px);
    background: #080808;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 14px;
    overflow: hidden;
    animation: wm-modal-in 0.28s ease;
    box-shadow: 0 40px 120px rgba(0,0,0,0.95), inset 0 1px 0 rgba(255,255,255,0.04);
    display: flex; flex-direction: column;
    max-height: calc(100vh - 40px);
  }

  /* ── Header ── */
  .wm-hdr {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    background: rgba(0,0,0,0.6);
    flex-shrink: 0;
  }
  .wm-hdr-logo { height: 28px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(255,255,255,0.08)); }
  .wm-hdr-logo-fallback {
    font-family: 'Anton', sans-serif; font-size: 13px; letter-spacing: 3px; color: #fff;
  }
  .wm-hdr-logo-fallback span { color: #e01010; }
  .wm-skip {
    font-family: 'Monda', sans-serif; font-size: 8px; letter-spacing: 2px;
    color: rgba(255,255,255,0.22); background: none; border: none;
    cursor: pointer; text-transform: uppercase; padding: 4px 6px; transition: color 0.15s;
  }
  .wm-skip:hover { color: rgba(255,255,255,0.55); }

  /* ── Tabs ── */
  .wm-tabs {
    display: flex; border-bottom: 1px solid rgba(255,255,255,0.07);
    background: rgba(0,0,0,0.5); flex-shrink: 0;
  }
  .wm-tab {
    flex: 1; font-family: 'Monda', sans-serif; font-size: 9px; letter-spacing: 2px;
    text-transform: uppercase; color: rgba(255,255,255,0.3);
    padding: 12px; text-align: center; cursor: pointer;
    border: none; background: transparent;
    border-bottom: 2px solid transparent;
    transition: color 0.2s, border-color 0.2s;
  }
  .wm-tab:hover { color: rgba(255,255,255,0.6); }
  .wm-tab.active { color: rgba(255,255,255,0.9); border-bottom-color: #e01010; }

  /* ── Slide area ── */
  .wm-slide-area { position: relative; height: clamp(340px, 42vh, 520px); flex-shrink: 0; overflow: hidden; }
  .wm-slide {
    position: absolute; inset: 0;
    opacity: 0; pointer-events: none; transform: translateX(30px);
    transition: opacity 0.28s ease, transform 0.28s ease;
  }
  .wm-slide.active { opacity: 1; pointer-events: all; transform: translateX(0); }

  /* ── Hero ── */
  .wm-hero {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; padding: 28px; text-align: center; position: relative; overflow: hidden;
  }
  .wm-hero-bg {
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(224,16,16,0.16) 0%, transparent 65%),
                radial-gradient(ellipse at 20% 100%, rgba(224,16,16,0.05) 0%, transparent 55%);
  }
  .wm-hero-accentline {
    position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, #e01010, transparent);
  }
  .wm-eyebrow {
    font-family: 'Monda', sans-serif; font-size: 9px; letter-spacing: 4px;
    color: #e01010; text-transform: uppercase; margin-bottom: 8px; position: relative;
  }
  .wm-hero-title {
    font-family: 'Anton', sans-serif; font-size: 2.8rem; letter-spacing: 4px;
    color: #fff; line-height: 1; position: relative;
  }
  .wm-hero-title span { color: #e01010; }
  .wm-hero-desc {
    font-family: 'Monda', sans-serif; font-size: 10px; color: rgba(255,255,255,0.35);
    line-height: 1.75; max-width: 360px; margin: 14px auto 22px; position: relative;
  }
  .wm-brand-strip { display: flex; gap: 6px; justify-content: center; position: relative; flex-wrap: wrap; }
  .wm-brand-pill {
    font-family: 'Anton', sans-serif; font-size: 8px; letter-spacing: 2px;
    padding: 4px 11px; border-radius: 4px; border: 1px solid;
  }

  /* ── Feature layout ── */
  .wm-feat { display: flex; flex-direction: column; height: 100%; }
  .wm-feat-visual {
    position: relative; overflow: hidden; background: #0c0c0c;
    border-bottom: 1px solid rgba(255,255,255,0.06); flex: 1;
  }
  .wm-feat-accentline { position: absolute; top: 0; left: 0; right: 0; height: 2px; z-index: 2; }
  .wm-feat-body { padding: 12px 20px 14px; display: flex; flex-direction: column; gap: 3px; flex-shrink: 0; }
  .wm-feat-num { font-family: 'Monda', sans-serif; font-size: 8px; letter-spacing: 2px; text-transform: uppercase; }
  .wm-feat-title { font-family: 'Anton', sans-serif; font-size: 1.15rem; letter-spacing: 2px; color: #fff; line-height: 1.1; }
  .wm-feat-desc { font-family: 'Monda', sans-serif; font-size: 9px; color: rgba(255,255,255,0.38); line-height: 1.65; }
  .wm-feat-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 3px; }
  .wm-feat-tag {
    font-family: 'Monda', sans-serif; font-size: 7px; letter-spacing: 1.5px;
    color: rgba(255,255,255,0.28); border: 1px solid rgba(255,255,255,0.1);
    padding: 2px 7px; border-radius: 3px; text-transform: uppercase;
  }

  /* ── GM Cards (slide 2) ── */
  .wm-gm-wrap {
    display: flex; gap: 8px; align-items: flex-end; justify-content: center;
    padding: 12px 14px; height: 100%;
  }
  .wm-gm-card {
    flex: 1; border-radius: 8px; overflow: hidden; border: 1px solid;
    display: flex; flex-direction: column;
  }
  .wm-gm-render {
    flex: 1; position: relative; overflow: hidden;
    display: flex; align-items: flex-end; justify-content: center;
    min-height: 160px;
  }
  .wm-gm-render-bg {
    position: absolute; inset: 0; background-size: cover; background-position: center;
    opacity: 0.2;
  }
  .wm-gm-render-grad {
    position: absolute; inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%);
  }
  .wm-gm-render-img {
    position: absolute; bottom: -25%; width: 100%; height: 220px;
    object-fit: cover; object-position: top center;
  }
  .wm-gm-render-initial {
    font-family: 'Anton', sans-serif; font-size: 32px; position: relative; z-index: 1;
  }
  .wm-gm-footer {
    padding: 7px 8px 8px; background: rgba(0,0,0,0.88);
    border-top: 1px solid rgba(255,255,255,0.07); text-align: center; flex-shrink: 0;
  }
  .wm-gm-badge {
    font-family: 'Anton', sans-serif; font-size: 8px; letter-spacing: 1.5px;
    display: inline-block; padding: 1px 7px; border-radius: 2px; border: 1px solid; margin-bottom: 4px;
  }
  .wm-gm-name { font-family: 'Anton', sans-serif; font-size: 10px; letter-spacing: 0.5px; color: #fff; }
  .wm-gm-role { font-family: 'Monda', sans-serif; font-size: 7px; letter-spacing: 1px; color: rgba(255,255,255,0.28); margin-top: 1px; }
  .wm-gm-custom {
    flex: 0 0 75px; border: 1px dashed rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.02); border-radius: 8px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px;
  }

  /* ── Booking cards (slide 3) ── */
  .wm-booking-wrap { display: flex; gap: 8px; padding: 10px 12px; height: 100%; align-items: stretch; }
  .wm-match-card {
    flex: 1; background: #100a00; border: 1px solid rgba(212,175,55,0.3);
    border-radius: 7px; overflow: hidden; display: flex; flex-direction: column;
  }
  .wm-promo-card {
    flex: 1; background: #100a00; border: 1px solid rgba(212,175,55,0.2);
    border-radius: 7px; overflow: hidden; display: flex; flex-direction: column;
  }
  .wm-card-hdr {
    padding: 6px 10px; border-bottom: 1px solid rgba(212,175,55,0.12);
    display: flex; align-items: baseline; gap: 6px;
  }
  .wm-card-type { font-family: 'Anton', sans-serif; font-size: 11px; letter-spacing: 1px; }
  .wm-card-sub { font-family: 'Monda', sans-serif; font-size: 7px; letter-spacing: 1px; color: rgba(212,175,55,0.55); }
  .wm-card-body { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px; }
  /* ── Wrestler Card (in-app style) ── */
  .wm-wcard {
    background: #0a0a0a; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 7px; overflow: hidden;
    display: flex; flex-direction: column; position: relative;
  }
  .wm-wcard-render {
    position: relative; overflow: hidden;
    display: flex; align-items: flex-end; justify-content: center;
    background: radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.05) 0%, transparent 70%);
  }
  .wm-wcard-img {
    position: absolute; bottom: -45%; left: 50%; transform: translateX(-50%);
    height: 160%; object-fit: contain;
  }
  .wm-wcard-grad { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 55%); }
  .wm-wcard-initial { font-family: 'Anton', sans-serif; color: rgba(255,255,255,0.1); position: relative; z-index: 1; margin-bottom: 10px; }
  .wm-wcard-xp {
    position: absolute; top: 5px; right: 5px;
    width: 20px; height: 20px; border-radius: 50%;
    background: rgba(0,0,0,0.75); border: 1px solid rgba(212,175,55,0.5);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Monda', sans-serif; font-size: 7px; font-weight: 700; color: rgba(212,175,55,0.9);
    z-index: 2;
  }
  .wm-wcard-champ { position: absolute; top: 5px; left: 5px; font-size: 11px; z-index: 2; }
  .wm-wcard-footer {
    padding: 5px 5px 6px; background: rgba(0,0,0,0.85);
    border-top: 1px solid rgba(255,255,255,0.07); flex-shrink: 0;
  }
  .wm-wcard-name {
    font-family: 'Anton', sans-serif; font-size: 8px; letter-spacing: 0.5px; color: #fff;
    text-align: center; margin-bottom: 4px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .wm-wcard-icons { display: flex; align-items: center; justify-content: center; gap: 3px; }
  .wm-wcard-icon {
    width: 14px; height: 14px; border-radius: 3px;
    display: flex; align-items: center; justify-content: center; font-size: 8px;
  }
  .wm-wcard-xpbar { height: 2px; border-radius: 2px; background: rgba(255,255,255,0.07); margin-top: 3px; overflow: hidden; }
  .wm-wcard-xpfill { height: 100%; border-radius: 2px; background: #a855f7; }
  .wm-vs { font-family: 'Anton', sans-serif; font-size: 1.1rem; color: #e01010; letter-spacing: 2px; flex-shrink: 0; }
  .wm-arrow { font-size: 14px; color: #e01010; flex-shrink: 0; }
  .wm-promo-hint { font-family: 'Monda', sans-serif; font-size: 8px; color: rgba(212,175,55,0.4); text-align: center; line-height: 1.5; max-width: 170px; }

  /* ── Story slide (slide 4) ── */
  .wm-story-wrap { display: flex; height: 100%; }
  .wm-rivalry-col { flex: 0 0 52%; border-right: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; }
  .wm-rv-topbar { background: rgba(0,0,0,0.7); padding: 7px 10px; border-bottom: 1px solid rgba(224,100,0,0.25); }
  .wm-rv-type { display: flex; align-items: center; gap: 6px; }
  .wm-rv-label { font-family: 'Anton', sans-serif; font-size: 9px; color: #e07020; letter-spacing: 2px; }
  .wm-rv-sub { font-family: 'Monda', sans-serif; font-size: 7px; color: rgba(255,255,255,0.28); letter-spacing: 1px; }
  .wm-rv-quote { font-family: 'Monda', sans-serif; font-size: 7px; color: rgba(255,255,255,0.28); font-style: italic; margin-top: 4px; line-height: 1.4; }
  .wm-rv-heat { display: flex; align-items: center; gap: 4px; margin-top: 5px; }
  .wm-rv-pip { width: 18px; height: 3px; border-radius: 2px; }
  .wm-rv-heat-lbl { font-family: 'Monda', sans-serif; font-size: 7px; color: rgba(255,255,255,0.35); letter-spacing: 1px; }
  .wm-rv-fighters {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
    background: radial-gradient(ellipse at 50% 60%, rgba(224,100,0,0.1) 0%, transparent 70%);
    padding: 8px 4px;
  }
  .wm-rv-fighter { display: flex; flex-direction: column; align-items: center; width: 150px; gap: 0; }
  .wm-rv-render { height: 190px; width: 140px; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
  .wm-rv-render-img { width: 100%; height: 100%; object-fit: contain; object-position: center center; }
  .wm-rv-initial { font-family: 'Anton', sans-serif; font-size: 26px; color: rgba(255,255,255,0.1); }
  .wm-rv-name { font-family: 'Anton', sans-serif; font-size: 12px; letter-spacing: 1.5px; color: #fff; text-align: center; padding: 5px 0 0; margin-top: 0; }
  .wm-rv-vs { font-family: 'Anton', sans-serif; font-size: 1.3rem; color: #e01010; letter-spacing: 3px; flex-shrink: 0; margin-bottom: 30px; }

  /* Big Moments */
  .wm-moments-col { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
  .wm-moments-hdr { padding: 7px 10px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; gap: 6px; }
  .wm-moments-title { font-family: 'Anton', sans-serif; font-size: 10px; letter-spacing: 2px; color: #fff; }
  .wm-moment { padding: 6px 10px; border-bottom: 1px solid rgba(255,255,255,0.04); }
  .wm-moment-champ { background: rgba(212,175,55,0.07); }
  .wm-moment-tag { font-family: 'Anton', sans-serif; font-size: 7px; letter-spacing: 1.5px; }
  .wm-moment-detail { font-family: 'Monda', sans-serif; font-size: 8px; color: rgba(255,255,255,0.42); margin-top: 2px; line-height: 1.4; }
  .wm-moment-detail strong { color: rgba(255,255,255,0.72); }

  /* ── Standings (slide 5) ── */
  .wm-standings-wrap { display: flex; flex-direction: column; height: 100%; }
  .wm-standings-topbar { padding: 7px 14px; border-bottom: 1px solid rgba(212,175,55,0.12); display: flex; align-items: center; justify-content: space-between; }
  .wm-standings-lbl { font-family: 'Monda', sans-serif; font-size: 7px; letter-spacing: 2px; color: rgba(255,255,255,0.22); text-transform: uppercase; }
  .wm-standings-season { font-family: 'Monda', sans-serif; font-size: 7px; color: rgba(255,255,255,0.2); }
  .wm-standings-list { flex: 1; display: flex; flex-direction: column; padding: 7px 12px; gap: 4px; }
  .wm-st-row { display: flex; align-items: center; gap: 7px; padding: 7px 10px; border-radius: 5px; border: 1px solid; position: relative; overflow: hidden; }
  .wm-st-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.1; }
  .wm-st-logo { height: 14px; object-fit: contain; position: relative; z-index: 1; flex-shrink: 0; }
  .wm-st-rank { font-family: 'Anton', sans-serif; font-size: 13px; min-width: 14px; position: relative; z-index: 1; }
  .wm-st-info { flex: 1; position: relative; z-index: 1; }
  .wm-st-gm { font-family: 'Monda', sans-serif; font-size: 9px; }
  .wm-st-fans { text-align: right; position: relative; z-index: 1; }
  .wm-st-num { font-family: 'Anton', sans-serif; font-size: 11px; }
  .wm-st-lbl { font-family: 'Monda', sans-serif; font-size: 6px; letter-spacing: 1px; color: rgba(255,255,255,0.22); text-transform: uppercase; }
  .wm-next-ple { margin: 0 12px 8px; background: rgba(212,175,55,0.06); border: 1px solid rgba(212,175,55,0.18); border-radius: 5px; padding: 6px 11px; }
  .wm-next-ple-lbl { font-family: 'Monda', sans-serif; font-size: 7px; letter-spacing: 2px; color: rgba(212,175,55,0.38); text-transform: uppercase; }
  .wm-next-ple-name { font-family: 'Anton', sans-serif; font-size: 9px; letter-spacing: 1px; color: rgba(212,175,55,0.8); }

  /* ── Patch notes ── */
  .wm-body { flex: 1; overflow-y: auto; min-height: 0; }
  .wm-body::-webkit-scrollbar { width: 3px; }
  .wm-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  .wm-notes { padding: 26px 30px; }
  .wm-notes-hdr { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
  .wm-notes-title { font-family: 'Anton', sans-serif; font-size: 1.1rem; letter-spacing: 2px; color: #fff; }
  .wm-version-badge {
    display: inline-block; font-family: 'Monda', sans-serif; font-size: 8px;
    letter-spacing: 2px; text-transform: uppercase;
    background: rgba(224,16,16,0.12); color: #e01010;
    border: 1px solid rgba(224,16,16,0.28); padding: 3px 9px; border-radius: 3px; margin-bottom: 20px;
  }
  .wm-note-group { margin-bottom: 18px; }
  .wm-note-group:last-child { margin-bottom: 0; }
  .wm-note-group-label {
    font-family: 'Monda', sans-serif; font-size: 8px; letter-spacing: 2.5px;
    color: rgba(255,255,255,0.2); text-transform: uppercase;
    margin-bottom: 9px; padding-bottom: 7px; border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .wm-note-item { display: flex; gap: 9px; margin-bottom: 8px; align-items: flex-start; }
  .wm-note-item:last-child { margin-bottom: 0; }
  .wm-note-dot { width: 5px; height: 5px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
  .wm-dot-new { background: #e01010; }
  .wm-dot-fix { background: rgba(255,255,255,0.25); }
  .wm-dot-soon { background: rgba(255,255,255,0.1); }
  .wm-note-text { font-family: 'Monda', sans-serif; font-size: 10px; color: rgba(255,255,255,0.42); line-height: 1.6; }
  .wm-note-text strong { color: rgba(255,255,255,0.78); font-weight: 700; }

  /* ── Footer ── */
  .wm-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 11px 18px 14px;
    border-top: 1px solid rgba(255,255,255,0.06);
    background: rgba(0,0,0,0.35); flex-shrink: 0;
  }
  .wm-dots { display: flex; gap: 6px; align-items: center; }
  .wm-dot-nav {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(255,255,255,0.15); border: none; cursor: pointer; padding: 0;
    transition: background 0.2s, transform 0.2s;
  }
  .wm-dot-nav.active { background: #e01010; transform: scale(1.4); }
  .wm-nav { display: flex; gap: 7px; align-items: center; }
  .wm-btn-ghost {
    font-family: 'Monda', sans-serif; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase;
    background: transparent; border: 1px solid rgba(255,255,255,0.15);
    color: rgba(255,255,255,0.4); padding: 8px 16px; border-radius: 4px; cursor: pointer; transition: all 0.15s;
  }
  .wm-btn-ghost:hover { border-color: rgba(255,255,255,0.4); color: rgba(255,255,255,0.75); }
  .wm-btn-primary {
    font-family: 'Monda', sans-serif; font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
    background: #e01010; border: 1px solid #e01010;
    color: #fff; padding: 8px 20px; border-radius: 4px; cursor: pointer; transition: all 0.15s;
  }
  .wm-btn-primary:hover { background: #c50e0e; border-color: #c50e0e; }
`;

// ── Wrestler mini-card ────────────────────────────────────────────────
function WCard({ render, initial, name, xp, champ, accentColor, renderH = 120, width = 100, xpPct = 60 }) {
  const [err, setErr] = React.useState(false);
  return (
    <div className="wm-wcard" style={{ width, borderColor: accentColor || 'rgba(255,255,255,0.12)' }}>
      <div className="wm-wcard-render" style={{ height: renderH }}>
        {!err && render ? (
          <img src={render} alt={name} className="wm-wcard-img" onError={() => setErr(true)} />
        ) : (
          <div className="wm-wcard-initial" style={{ fontSize: renderH * 0.22 }}>{initial}</div>
        )}
        <div className="wm-wcard-grad" />
      </div>
      <div className="wm-wcard-xp" style={accentColor ? { borderColor: accentColor + '99', color: accentColor } : {}}>{xp}</div>
      {champ && <div className="wm-wcard-champ">👑</div>}
      <div className="wm-wcard-footer">
        <div className="wm-wcard-name">{name}</div>
        <div className="wm-wcard-icons">
          <div className="wm-wcard-icon" style={{ background: 'rgba(255,255,255,0.08)' }}>⭐</div>
          <div className="wm-wcard-icon" style={{ background: champ ? 'rgba(34,197,94,0.18)' : 'rgba(224,16,16,0.18)' }}>🛡</div>
          <div className="wm-wcard-icon" style={{ background: 'rgba(168,85,247,0.18)', fontSize: '7px', gap: '1px', display: 'flex', alignItems: 'center' }}>
            🔨<span style={{ fontFamily: "'Monda',sans-serif", fontSize: '5px', color: 'rgba(168,85,247,0.9)' }}>5</span>
          </div>
        </div>
        <div className="wm-wcard-xpbar">
          <div className="wm-wcard-xpfill" style={{ width: xpPct + '%', background: accentColor || '#a855f7' }} />
        </div>
      </div>
    </div>
  );
}

// ── GM Card ───────────────────────────────────────────────────────────
function GMCard({ render, bg, initial, badge, badgeColor, borderColor, name, role }) {
  const [renderErr, setRenderErr] = React.useState(false);
  const [bgErr, setBgErr] = React.useState(false);
  return (
    <div className="wm-gm-card" style={{ borderColor }}>
      <div className="wm-gm-render">
        {bg && !bgErr && (
          <div className="wm-gm-render-bg"
            style={{ backgroundImage: `url(${bg})` }}
            onError={() => setBgErr(true)} />
        )}
        <div className="wm-gm-render-grad" />
        {!renderErr && render ? (
          <img src={render} alt={name} className="wm-gm-render-img" onError={() => setRenderErr(true)} />
        ) : (
          <div className="wm-gm-render-initial" style={{ color: badgeColor + '44' }}>{initial}</div>
        )}
      </div>
      <div className="wm-gm-footer">
        <div className="wm-gm-badge" style={{ color: badgeColor, borderColor: badgeColor + '66', fontSize: badge.length > 3 ? '6px' : '8px' }}>
          {badge}
        </div>
        <div className="wm-gm-name">{name}</div>
        <div className="wm-gm-role">{role}</div>
      </div>
    </div>
  );
}

// ── Rivalry fighter ───────────────────────────────────────────────────
function RVFighter({ render, initial, name }) {
  const [err, setErr] = React.useState(false);
  return (
    <div className="wm-rv-fighter">
      <div className="wm-rv-render">
        {!err && render ? (
          <img src={render} alt={name} className="wm-rv-render-img" onError={() => setErr(true)} />
        ) : (
          <div className="wm-rv-initial">{initial}</div>
        )}
      </div>
      <div className="wm-rv-name">{name}</div>
    </div>
  );
}

// ── Standing row ──────────────────────────────────────────────────────
function StandingRow({ rank, logo, logoBg, brandBg, gm, fans, trophy, accent, dim }) {
  const [bgErr, setBgErr] = React.useState(false);
  const [logoErr, setLogoErr] = React.useState(false);
  return (
    <div className="wm-st-row" style={{
      borderColor: accent + '55',
      background: accent + '18',
      opacity: dim ? 0.55 : 1,
    }}>
      {brandBg && !bgErr && (
        <img src={brandBg} alt="" className="wm-st-bg" onError={() => setBgErr(true)} />
      )}
      <div className="wm-st-rank" style={{ color: trophy ? '#d4af37' : 'rgba(255,255,255,0.3)' }}>{rank}</div>
      {logo && !logoErr ? (
        <img src={logo} alt="" className="wm-st-logo" onError={() => setLogoErr(true)} />
      ) : (
        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '7px', letterSpacing: '1px', color: accent, border: `1px solid ${accent}55`, padding: '1px 5px', borderRadius: '2px', position: 'relative', zIndex: 1, flexShrink: 0 }}>
          {logoBg}
        </div>
      )}
      <div className="wm-st-info">
        <div className="wm-st-gm" style={{ color: trophy ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.5)' }}>{gm}</div>
      </div>
      {trophy && <span style={{ fontSize: '12px', position: 'relative', zIndex: 1 }}>🏆</span>}
      <div className="wm-st-fans">
        <div className="wm-st-num" style={{ color: trophy ? '#fff' : 'rgba(255,255,255,0.45)' }}>{fans}</div>
        <div className="wm-st-lbl">FANS</div>
      </div>
    </div>
  );
}

// ── PatchNotesContent ─────────────────────────────────────────────────
function PatchNotesContent({ notes }) {
  const DOT_CLASS = { new: 'wm-dot-new', fix: 'wm-dot-fix', soon: 'wm-dot-soon' };
  return (
    <div className="wm-notes">
      {notes.map((entry) => (
        <div key={entry.version} style={{ marginBottom: notes.length > 1 ? '32px' : 0 }}>
          <div className="wm-notes-hdr">
            <div className="wm-notes-title">WHAT'S NEW</div>
            <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '9px', color: 'rgba(255,255,255,0.2)' }}>{entry.date}</div>
          </div>
          <div className="wm-version-badge">VERSION {entry.version} — {entry.label.toUpperCase()}</div>
          {entry.groups.map((group) => (
            <div key={group.title} className="wm-note-group">
              <div className="wm-note-group-label">{group.title}</div>
              {group.items.map((item, i) => {
                const parts = item.text.split(' — ');
                const bold = parts.length > 1 ? parts[0] : null;
                const rest = parts.length > 1 ? parts.slice(1).join(' — ') : item.text;
                return (
                  <div key={i} className="wm-note-item">
                    <div className={`wm-note-dot ${DOT_CLASS[group.type] || 'wm-dot-fix'}`} />
                    <div className="wm-note-text">
                      {bold ? <><strong>{bold}</strong>{' — '}{rest}</> : rest}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Slide configs ─────────────────────────────────────────────────────
const ACCENTS = [
  '#e01010',
  '#c0392b',
  '#1a6bb5',
  '#d4af37',
  'rgba(255,255,255,0.3)',
];

// ── Main WelcomeModal ─────────────────────────────────────────────────
export default function WelcomeModal({ onClose, showTourTab }) {
  const [user] = useAuthState(auth);
  const [activeTab, setActiveTab] = useState(showTourTab ? 'tour' : 'notes');
  const [slideIdx, setSlideIdx] = useState(0);
  const [logoErr, setLogoErr] = useState(false);
  const TOTAL = 5;

  const goTo = useCallback((next, dir) => {
    if (next === slideIdx) return;
    const direction = dir !== undefined ? dir : (next > slideIdx ? 1 : -1);
    const oldEl = document.getElementById('wm-slide-' + slideIdx);
    if (oldEl) {
      oldEl.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
      oldEl.style.opacity = '0';
      oldEl.style.transform = direction > 0 ? 'translateX(-28px)' : 'translateX(28px)';
      oldEl.classList.remove('active');
    }
    const newEl = document.getElementById('wm-slide-' + next);
    if (newEl) {
      newEl.style.transition = 'none';
      newEl.style.transform = direction > 0 ? 'translateX(28px)' : 'translateX(-28px)';
      newEl.style.opacity = '0';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newEl.style.transition = 'opacity 0.28s ease, transform 0.28s ease';
          newEl.style.transform = 'translateX(0)';
          newEl.style.opacity = '1';
          newEl.classList.add('active');
        });
      });
    }
    setSlideIdx(next);
  }, [slideIdx]);

  const handleNext = () => {
    if (slideIdx < TOTAL - 1) goTo(slideIdx + 1, 1);
    else setActiveTab('notes');
  };

  const handleClose = async () => {
    if (user) {
      try {
        const ref = doc(db, 'users', user.uid);
        const snap = await getDoc(ref);
        const data = { hasSeenWelcome: true, lastSeenPatchVersion: CURRENT_VERSION, updatedAt: new Date().toISOString() };
        snap.exists() ? await updateDoc(ref, data) : await setDoc(ref, { ...data, email: user.email, createdAt: new Date().toISOString() });
      } catch (e) { console.error('WelcomeModal:', e); }
    }
    onClose();
  };

  const isTour = activeTab === 'tour';
  const accent = ACCENTS[slideIdx] || ACCENTS[0];

  return (
    <>
      <style>{css}</style>
      <div className="wm-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
        <div className="wm-modal">

          {/* Header */}
          <div className="wm-hdr">
            {logoErr ? (
              <div className="wm-hdr-logo-fallback">GM <span>RIVALRY</span> ENGINE</div>
            ) : (
              <img src={mainLogo} alt="GM Rivalry Engine" className="wm-hdr-logo"
                onError={() => setLogoErr(true)} />
            )}
            <button className="wm-skip" onClick={() => isTour && slideIdx > 0 ? setActiveTab('notes') : handleClose()}>
              {isTour && slideIdx > 0 ? 'SKIP TOUR' : 'SKIP'}
            </button>
          </div>

          {/* Tabs */}
          {showTourTab && (
            <div className="wm-tabs">
              <button className={`wm-tab${activeTab === 'tour' ? ' active' : ''}`}
                onClick={() => { setActiveTab('tour'); setSlideIdx(0); }}>
                QUICK TOUR
              </button>
              <button className={`wm-tab${activeTab === 'notes' ? ' active' : ''}`}
                onClick={() => setActiveTab('notes')}>
                PATCH NOTES
              </button>
            </div>
          )}

          {isTour ? (
            <>
              <div className="wm-slide-area">

                {/* ── Slide 0: Hero ── */}
                <div id="wm-slide-0" className="wm-slide active">
                  <div className="wm-hero">
                    <div className="wm-hero-bg" />
                    <div className="wm-eyebrow">Welcome to</div>
                    <div className="wm-hero-title">GM RIVALRY<br /><span>ENGINE</span></div>
                    <div className="wm-hero-desc">
                      The ultimate WWE 2K MyGM companion. Book your shows, build rivalries, manage your roster — and lead your brand to glory.
                    </div>
                    <div className="wm-brand-strip">
                      {[
                        { label: 'RAW', c: '#c0392b' },
                        { label: 'SMACKDOWN', c: '#5b9bd5' },
                        { label: 'NXT', c: '#a8a9ad' },
                        { label: '+ MORE', c: 'rgba(255,255,255,0.22)', dim: true },
                      ].map(b => (
                        <div key={b.label} className="wm-brand-pill" style={{ borderColor: b.c + (b.dim ? '' : '55'), background: b.c + (b.dim ? '08' : '12'), color: b.c }}>
                          {b.label}
                        </div>
                      ))}
                    </div>
                    <div className="wm-hero-accentline" />
                  </div>
                </div>

                {/* ── Slide 1: Brand ── */}
                <div id="wm-slide-1" className="wm-slide">
                  <div className="wm-feat">
                    <div className="wm-feat-visual">
                      <div className="wm-feat-accentline" style={{ background: '#c0392b' }} />
                      <div className="wm-gm-wrap">
                        <GMCard render={gmAdamPearce} bg={bgRaw} initial="AP"
                          badge="RAW" badgeColor="#c0392b" borderColor="rgba(192,57,43,0.45)"
                          name="ADAM PEARCE" role="General Manager" />
                        <GMCard render={gmNickAldis} bg={bgSmackDown} initial="NA"
                          badge="SMACKDOWN" badgeColor="#5b9bd5" borderColor="rgba(91,155,213,0.4)"
                          name="NICK ALDIS" role="General Manager" />
                        <GMCard render={gmShawnMichaels} bg={bgNXT} initial="SM"
                          badge="NXT" badgeColor="#a8a9ad" borderColor="rgba(168,169,173,0.35)"
                          name="SHN MICHAELS" role="General Manager" />
                        <div className="wm-gm-custom">
                          <div style={{ fontSize: '20px', color: 'rgba(255,255,255,0.18)' }}>＋</div>
                          <div style={{ fontFamily: "'Monda', sans-serif", fontSize: '7px', letterSpacing: '1px', color: 'rgba(255,255,255,0.18)', textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.5 }}>Custom<br />Brand</div>
                        </div>
                      </div>
                    </div>
                    <div className="wm-feat-body" style={{ background: 'rgba(192,57,43,0.04)' }}>
                      <div className="wm-feat-num" style={{ color: '#c0392b' }}>01 / 04</div>
                      <div className="wm-feat-title">BUILD YOUR BRAND</div>
                      <div className="wm-feat-desc">Pick a brand, choose your GM, draft your roster over 400+ WWE superstars. Custom brands and custom content fully supported.</div>
                      <div className="wm-feat-tags">
                        <span className="wm-feat-tag" style={{ color: 'rgba(192,57,43,0.8)', borderColor: 'rgba(192,57,43,0.3)' }}>WWE 2K26</span>
                        <span className="wm-feat-tag">WWE 2K25</span>
                        <span className="wm-feat-tag">Draft System</span>
                        <span className="wm-feat-tag">Custom Content</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Slide 2: Booking ── */}
                <div id="wm-slide-2" className="wm-slide">
                  <div className="wm-feat">
                    <div className="wm-feat-visual">
                      <div className="wm-feat-accentline" style={{ background: '#1a6bb5' }} />
                      <div className="wm-booking-wrap">
                        {/* Match card */}
                        <div className="wm-match-card">
                          <div className="wm-card-hdr">
                            <span className="wm-card-type" style={{ color: '#e01010' }}>MAIN EVENT</span>
                            <span className="wm-card-sub">Singles Match</span>
                          </div>
                          <div className="wm-card-body">
                            <WCard render={SETH_RENDER} initial="SR" name="SETH ROLLINS"
                              xp={15} xpPct={60} width={98} renderH={130} />
                            <div className="wm-vs">VS</div>
                            <WCard render={ROMAN_RENDER} initial="RR" name="ROMAN REIGNS"
                              xp={20} champ xpPct={85} accentColor="#d4af37" width={98} renderH={130} />
                          </div>
                        </div>
                        {/* Promo card */}
                        <div className="wm-promo-card">
                          <div className="wm-card-hdr">
                            <span className="wm-card-type" style={{ color: '#d4af37' }}>PROMO</span>
                            <span className="wm-card-sub">Call-Out</span>
                          </div>
                          <div className="wm-card-body" style={{ flexDirection: 'column', gap: '10px' }}>
                            <div className="wm-promo-hint">Chance to begin a 1v1 Rivalry or increase the level of an existing one.</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                              <WCard render={IYO_RENDER} initial="IS" name="IYO SKY"
                                xp={15} xpPct={55} width={86} renderH={110} />
                              <div className="wm-arrow">→</div>
                              <WCard render={LIV_RENDER} initial="LM" name="LIV MORGAN"
                                xp={10} xpPct={35} accentColor="rgba(224,16,16,0.6)" width={86} renderH={110} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="wm-feat-body" style={{ background: 'rgba(26,107,181,0.04)' }}>
                      <div className="wm-feat-num" style={{ color: '#5b9bd5' }}>02 / 04</div>
                      <div className="wm-feat-title">BOOK YOUR SHOWS</div>
                      <div className="wm-feat-desc">Book matches, promos and segments each week. Set stipulations, run the show, and track your fan growth. PLEs fully supported.</div>
                      <div className="wm-feat-tags">
                        <span className="wm-feat-tag" style={{ color: 'rgba(91,155,213,0.8)', borderColor: 'rgba(91,155,213,0.3)' }}>Weekly Shows</span>
                        <span className="wm-feat-tag">PLEs</span>
                        <span className="wm-feat-tag">Match Types</span>
                        <span className="wm-feat-tag">Show Flow</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Slide 3: Story ── */}
                <div id="wm-slide-3" className="wm-slide">
                  <div className="wm-feat">
                    <div className="wm-feat-visual" style={{ padding: 0 }}>
                      <div className="wm-feat-accentline" style={{ background: '#d4af37' }} />
                      <div className="wm-story-wrap">
                        {/* Rivalry col */}
                        <div className="wm-rivalry-col">
                          <div className="wm-rv-topbar">
                            <div className="wm-rv-type">
                              <span style={{ fontSize: '10px' }}>⚔</span>
                              <span className="wm-rv-label">PERSONAL RIVALRY</span>
                              <span className="wm-rv-sub">· BUILD</span>
                            </div>
                            <div className="wm-rv-quote">"The bad blood between Ethan Page and Je'Von Evans spills over week after week."</div>
                            <div className="wm-rv-heat">
                              {[1,1,0,0,0].map((on, i) => (
                                <div key={i} className="wm-rv-pip" style={{ background: on ? '#e01010' : 'rgba(255,255,255,0.12)' }} />
                              ))}
                              <span className="wm-rv-heat-lbl">Niv.2 — Intense</span>
                            </div>
                          </div>
                          <div className="wm-rv-fighters">
                            <RVFighter render={ETHAN_RENDER} initial="EP" name="ETHAN PAGE" />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginBottom: '30px' }}>
                              <div className="wm-rv-vs">VS</div>
                            </div>
                            <RVFighter render={JEVON_RENDER} initial="JE" name="JE'VON EVANS" />
                          </div>
                        </div>
                        {/* Big Moments col */}
                        <div className="wm-moments-col">
                          <div className="wm-moments-hdr">
                            <span style={{ fontSize: '11px' }}>🎬</span>
                            <span className="wm-moments-title">BIG MOMENTS</span>
                          </div>
                          {[
                            { type: 'champ', tag: 'NEW CHAMPION', detail: <><strong>Roman Reigns</strong> — WWE Championship</> },
                            { type: 'heat',  tag: '🔥 HEATED UP', detail: <><strong>Ethan Page & Je'Von Evans</strong></> },
                            { type: 'ended', tag: '✓ ENDED',      detail: <>Iyo Sky & Liv Morgan</> },
                            { type: 'heat',  tag: '🔥 HEATED UP', detail: <><strong>Seth Rollins & Gunther</strong></> },
                            { type: 'ended', tag: '✓ ENDED',      detail: <>Sami Zayn & Chad Gable</>, last: true },
                          ].map((m, i) => (
                            <div key={i} className={`wm-moment${m.type === 'champ' ? ' wm-moment-champ' : ''}`} style={m.last ? { borderBottom: 'none' } : {}}>
                              <div className="wm-moment-tag" style={{ color: m.type === 'champ' ? '#d4af37' : m.type === 'heat' ? '#e01010' : 'rgba(255,255,255,0.3)' }}>{m.tag}</div>
                              <div className="wm-moment-detail">{m.detail}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="wm-feat-body" style={{ background: 'rgba(212,175,55,0.03)' }}>
                      <div className="wm-feat-num" style={{ color: '#d4af37' }}>03 / 04</div>
                      <div className="wm-feat-title">BUILD YOUR STORY</div>
                      <div className="wm-feat-desc">Create rivalries, build heat week by week, assign one of 20 unique storylines. Milestones unlock Hall of Fame trophies.</div>
                      <div className="wm-feat-tags">
                        <span className="wm-feat-tag" style={{ color: 'rgba(212,175,55,0.8)', borderColor: 'rgba(212,175,55,0.3)' }}>Rivalries</span>
                        <span className="wm-feat-tag">20 Storylines</span>
                        <span className="wm-feat-tag">Championships</span>
                        <span className="wm-feat-tag">Hall of Fame</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Slide 4: Season ── */}
                <div id="wm-slide-4" className="wm-slide">
                  <div className="wm-feat">
                    <div className="wm-feat-visual" style={{ padding: 0 }}>
                      <div className="wm-feat-accentline" style={{ background: 'rgba(255,255,255,0.25)' }} />
                      <div className="wm-standings-wrap">
                        <div className="wm-standings-topbar">
                          <div className="wm-standings-lbl">Standings · Based on Fans</div>
                          <div className="wm-standings-season">Season 1 · Week 47</div>
                        </div>
                        <div className="wm-standings-list">
                          <StandingRow rank="1" logo={logoRaw} logoBg="RAW" brandBg={bgRaw}
                            gm="Adam Pearce" fans="1 521 430" trophy accent="#c0392b" />
                          <StandingRow rank="2" logo={logoSmackDown} logoBg="SD" brandBg={bgSmackDown}
                            gm="Nick Aldis" fans="1 389 210" accent="#1a6bb5" />
                          <StandingRow rank="3" logo={logoNXT} logoBg="NXT" brandBg={bgNXT}
                            gm="Shawn Michaels" fans="1 247 880" accent="#a8a9ad" dim />
                          <StandingRow rank="4" logo={logoECW} logoBg="ECW"
                            gm="Paul Heyman" fans="1 098 540" accent="rgba(255,255,255,0.3)" dim />
                        </div>
                        <div className="wm-next-ple">
                          <div className="wm-next-ple-lbl">Next PLE</div>
                          <div className="wm-next-ple-name">WRESTLEMANIA · WEEK 50</div>
                        </div>
                      </div>
                    </div>
                    <div className="wm-feat-body">
                      <div className="wm-feat-num" style={{ color: 'rgba(255,255,255,0.3)' }}>04 / 04</div>
                      <div className="wm-feat-title">WIN THE SEASON</div>
                      <div className="wm-feat-desc">Grow your fanbase, dominate the standings, and induct your legends in the Hall of Fame. Up to 4 save slots, multiple careers simultaneously.</div>
                      <div className="wm-feat-tags">
                        <span className="wm-feat-tag" style={{ color: 'rgba(255,255,255,0.45)', borderColor: 'rgba(255,255,255,0.18)' }}>Standings</span>
                        <span className="wm-feat-tag">4 Save Slots</span>
                        <span className="wm-feat-tag">Season Recap</span>
                        <span className="wm-feat-tag">HOF Ceremony</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Footer tour */}
              <div className="wm-footer">
                <div className="wm-dots">
                  {[0,1,2,3,4].map(i => (
                    <button key={i} className={`wm-dot-nav${i === slideIdx ? ' active' : ''}`}
                      onClick={() => goTo(i, i > slideIdx ? 1 : -1)} aria-label={`Slide ${i+1}`} />
                  ))}
                </div>
                <div className="wm-nav">
                  {slideIdx > 0 && (
                    <button className="wm-btn-ghost" onClick={() => goTo(slideIdx - 1, -1)}>BACK</button>
                  )}
                  {slideIdx === 0 ? (
                    <button className="wm-btn-ghost" onClick={() => goTo(1, 1)}>DISCOVER →</button>
                  ) : (
                    <button className="wm-btn-primary" onClick={handleNext}>
                      {slideIdx === TOTAL - 1 ? "WHAT'S NEW →" : 'NEXT →'}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="wm-body">
                <PatchNotesContent notes={PATCH_NOTES} />
              </div>
              <div className="wm-footer" style={{ justifyContent: 'flex-end' }}>
                <button className="wm-btn-primary" onClick={handleClose}>LET'S GO →</button>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────
export function useWelcomeModal(user) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!user) { setState({ show: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        const hasSeenWelcome = !!data.hasSeenWelcome;
        const lastSeenVersion = data.lastSeenPatchVersion || null;
        const hasNewPatch = lastSeenVersion !== CURRENT_VERSION;
        if (!hasSeenWelcome) setState({ show: true, showTourTab: true });
        else if (hasNewPatch) setState({ show: true, showTourTab: false });
        else setState({ show: false });
      } catch (e) {
        console.error('useWelcomeModal:', e);
        setState({ show: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);
  return state;
}
